import type { StandardErrorResponse } from '@leanpay/services'
import { isStandardErrorResponse, logoutUser } from '@leanpay/services'

const DEFAULT_MAX_RETRIES = 5

const HTTP_UNAUTHORIZED = 401
const HTTP_CLIENT_ERROR_START = 400
const HTTP_INTERNAL_SERVER_ERROR = 500

/**
 * Creates a standard retry handler for query hooks that handles StandardErrorResponse properly.
 *
 * Logs the user out on 401 and never retries other 4xx or a 500 (the request will
 * not start succeeding). Gateway/availability errors (502, 503, 504) and transport
 * failures are retried up to `maxRetries`.
 *
 * @param maxRetries Maximum number of retry attempts (default: 5)
 * @returns Retry handler function that can be used with useMutation or useQuery
 */
export const createStandardRetryHandler = (maxRetries = DEFAULT_MAX_RETRIES) => {
    return (count: number, error: Error | StandardErrorResponse) => {
        if (isStandardErrorResponse(error)) {
            if (error.status === HTTP_UNAUTHORIZED) {
                logoutUser()
                return false
            }

            // A 500 is an unhandled server exception, so a retry hits the same failure.
            if (error.status === HTTP_INTERNAL_SERVER_ERROR) {
                return false
            }

            if (
                error.status &&
                error.status >= HTTP_CLIENT_ERROR_START &&
                error.status < HTTP_INTERNAL_SERVER_ERROR
            ) {
                return false
            }

            return count < maxRetries
        }

        return count < maxRetries
    }
}

/** Retry delay the apps standardised on for mutation retries. */
export const STANDARD_RETRY_DELAY_MS = 500
